import {
  Atom,
  Brain,
  CircleDot,
  Dna,
  Droplets,
  FlaskRound,
  FlowerIcon,
  Leaf,
  Moon,
  Sparkles,
  Shield,
  Sun,
  Zap,
  Waves,
  Beaker,
  Heart,
  type LucideIcon,
} from "lucide-react";

export type IngredientBenefit = {
  /** When set, row opens the ResearchDrawer at this ingredient. */
  slug?: string;
  name: string;
  dose: string;
  benefit: string;
  icon: LucideIcon;
};

export type FormulaMeta = {
  version: string;
  tag: string;
};

export const FORMULA_META: Record<"liver" | "mag" | "ova", FormulaMeta> = {
  liver: { version: "v3.2", tag: "Triple Liposomal" },
  mag: { version: "v2.1", tag: "Multi-Stage Mineral" },
  ova: { version: "v4.2", tag: "Endocrine Core" },
};

export const INGREDIENT_BENEFITS: Record<"liver" | "mag" | "ova", IngredientBenefit[]> = {
  liver: [
    { slug: "nac", name: "N-Acetyl Cysteine (NAC)", dose: "600 mg", benefit: "Glutathione precursor · Phase II detox", icon: Droplets },
    { slug: "silymarin", name: "Liposomal Milk Thistle (Silymarin)", dose: "350 mg Eq", benefit: "Hepatocyte membrane stabilization", icon: Leaf },
    { slug: "alpha-lipoic-acid", name: "Liposomal Alpha-Lipoic Acid", dose: "100 mg", benefit: "Universal antioxidant network", icon: Atom },
    { slug: "curcumin", name: "Liposomal Curcumin", dose: "100 mg", benefit: "Systemic inflammation modulation", icon: FlowerIcon },
    { slug: "probiotics", name: "Probiotic Consortium", dose: "10B CFU", benefit: "Gut-liver axis colonization", icon: CircleDot },
    { name: "Bio-Absorb Matrix", dose: "Proprietary", benefit: "Proprietary absorption blend", icon: Sparkles },
  ],
  mag: [
    { slug: "magnesium-bisglycinate", name: "Liposomal Magnesium Bisglycinate", dose: "200 mg", benefit: "Phase 3: deep cellular fusion", icon: Moon },
    { name: "Aquamin® Marine Magnesium", dose: "100 mg", benefit: "Phase 2: sustained multi-mineral", icon: Waves },
    { name: "Magnesium Chloride", dose: "60 mg", benefit: "Phase 1: immediate ionization", icon: Zap },
    { name: "Magnesium Aspartate", dose: "50 mg", benefit: "Phase 2: metabolic intermediate", icon: Beaker },
    { name: "Magnesium Gluconate", dose: "30 mg", benefit: "Phase 1: high-solubility uptake", icon: Droplets },
    { name: "Zinc & Copper Bisglycinate", dose: "12.5 mg", benefit: "Mineral antagonist balancing", icon: Shield },
    { name: "Cofactor Complex (P-5-P / D3)", dose: "3 mg / 600 IU", benefit: "Activates magnesium utilisation · neuromuscular support", icon: Brain },
    { name: "Bio-Absorb Matrix", dose: "Proprietary", benefit: "Proprietary absorption blend", icon: Sparkles },
  ],
  ova: [
    { slug: "myo-inositol", name: "Inositol Complex (Myo : D-Chiro 40:1)", dose: "2050 mg", benefit: "Insulin sensitivity modulation", icon: FlaskRound },
    { slug: "berberine", name: "Berberine HCl", dose: "300 mg", benefit: "Metabolic & glycemic support", icon: Sparkles },
    { name: "Quatrefolic® (L-5-MTHF)", dose: "400 mcg", benefit: "Bioactive methyl folate donor", icon: Dna },
    { slug: "vitamin-d3", name: "Vitamin D3 (Cholecalciferol)", dose: "600 IU (15 mcg)", benefit: "Calcium absorption · bone, muscle and immune support", icon: Shield },
    { name: "Magnesium Bisglycinate", dose: "100 mg", benefit: "Chelated mineral co-factor", icon: Heart },
    { name: "Zinc Bisglycinate", dose: "10 mg", benefit: "Hormonal & ovulatory support", icon: Shield },
    { name: "Chromium Picolinate", dose: "200 mcg", benefit: "Glucose transporter support", icon: Atom },
    { name: "Bio-Absorb Matrix", dose: "Proprietary", benefit: "Proprietary absorption blend", icon: Sparkles },
  ],
};

export function getFormulaKeyForHandle(handle: string): "liver" | "mag" | "ova" | null {
  const h = handle.toLowerCase();
  if (h.includes("liver") || h.includes("biome")) return "liver";
  if (h.includes("mag") || h.includes("absorb")) return "mag";
  if (h.includes("ova") || h.includes("inositol")) return "ova";
  return null;
}

export function getIngredientBenefitsForHandle(handle: string): IngredientBenefit[] | null {
  const key = getFormulaKeyForHandle(handle);
  return key ? INGREDIENT_BENEFITS[key] : null;
}

export function getFormulaMetaForHandle(handle: string): FormulaMeta | null {
  const key = getFormulaKeyForHandle(handle);
  return key ? FORMULA_META[key] : null;
}
